import React, { useState, useEffect } from 'react';
import { Modal, Select, Button, Row, Col, message, Spin, Divider } from 'antd';
import { PlusOutlined, MinusCircleOutlined } from '@ant-design/icons';
import { Link } from 'lucide-react';
import { get, post } from '../../utils/fetch';
import styles from './JiraUserMappingModal.module.css';

interface Props {
  open: boolean;
  onClose: () => void;
  organizationId: number;
  integrationId: string | null;
  projectIntegrationId: string | null;
  siteId: string | null;
  bugherdUsers: any[];
  container?: HTMLElement | (() => HTMLElement);
}

const JiraUserMappingModal: React.FC<Props> = ({
  open,
  onClose,
  organizationId,
  integrationId,
  projectIntegrationId,
  siteId,
  bugherdUsers,
  container
}) => {
  const [loading, setLoading] = useState(false);
  const [jiraUsers, setJiraUsers] = useState<any[]>([]);
  const [mappings, setMappings] = useState<{ jiraUserId: string; bugherdUserId: string }[]>(
    [{ jiraUserId: '', bugherdUserId: '' }]
  );

  useEffect(() => {
    if (open && integrationId && siteId && siteId !== 'undefined') {
      loadData();
    }
  }, [open, integrationId, siteId]);

  const loadData = async () => {
    setLoading(true);
    await Promise.all([fetchJiraUsers(), fetchCurrentSettings()]);
    setLoading(false);
  };

  const fetchJiraUsers = async () => {
    try {
      const results = await get(
        `/organizations/${organizationId}/integrations/jira_users?id=${integrationId}&site_id=${siteId}`
      );
      // Format users to include email in the label for better searchability
      const formattedUsers = results.map((user: any) => ({
        ...user,
        label: user.email ? `${user.label} (${user.email})` : user.label,
        originalLabel: user.label // Keep original label for mapping comparison
      }));
      setJiraUsers(formattedUsers);
    } catch (error) {
      message.error('Failed to fetch Jira users.');
    }
  };

  const fetchCurrentSettings = async () => {
    if (!projectIntegrationId || projectIntegrationId === 'undefined') {
      setMappings([{ jiraUserId: '', bugherdUserId: '' }]);
      return;
    }
    try {
      // We can get the project integration settings to initialize mappings
      const result = await get(
        `/organizations/${organizationId}/integrations/${integrationId}/project_integrations/${projectIntegrationId}`
      );
      if (result?.settings?.user_mapping) {
        const initialMappings = Object.entries(result.settings.user_mapping).map(
          ([jiraId, bhId]) => ({
            jiraUserId: jiraId,
            bugherdUserId: String(bhId)
          })
        );
        setMappings(initialMappings.length > 0 ? initialMappings : [{ jiraUserId: '', bugherdUserId: '' }]);
      } else {
        setMappings([{ jiraUserId: '', bugherdUserId: '' }]);
      }
    } catch (error) {
      console.error('Failed to fetch current settings', error);
      setMappings([{ jiraUserId: '', bugherdUserId: '' }]);
    }
  };

  const handleAddMapping = () => {
    setMappings([...mappings, { jiraUserId: '', bugherdUserId: '' }]);
  };

  const handleRemoveMapping = (index: number) => {
    const newMappings = mappings.filter((_, i) => i !== index);
    setMappings(newMappings);
    saveMappings(newMappings);
  };

  const handleMappingChange = (index: number, field: 'jiraUserId' | 'bugherdUserId', value: string) => {
    const newMappings = [...mappings];
    newMappings[index][field] = value;
    setMappings(newMappings);

    if (newMappings[index].jiraUserId && newMappings[index].bugherdUserId) {
      saveMappings(newMappings);
    }
  };

  const saveMappings = async (currentMappings: { jiraUserId: string; bugherdUserId: string }[]) => {
    if (!projectIntegrationId || projectIntegrationId === 'undefined') {
      message.error('Unable to save user mappings until the Jira integration settings have been saved.');
      return;
    }

    try {
      const usedJiraUserIds = new Set<string>();
      const usedBugherdUserIds = new Set<string>();
      const userMappingPayload = currentMappings.reduce((acc, m) => {
        if (
          m.jiraUserId &&
          m.bugherdUserId &&
          !usedJiraUserIds.has(m.jiraUserId) &&
          !usedBugherdUserIds.has(m.bugherdUserId)
        ) {
          acc[m.jiraUserId] = m.bugherdUserId;
          usedJiraUserIds.add(m.jiraUserId);
          usedBugherdUserIds.add(m.bugherdUserId);
        }
        return acc;
      }, {} as Record<string, string>);

      await post(
        `/organizations/${organizationId}/integrations/${integrationId}/project_integrations/save_settings?integration_code=Jira`,
        {
          project_integration_id: projectIntegrationId,
          settings: {
            user_mapping: userMappingPayload
          }
        }
      );
    } catch (error) {
      message.error('Failed to save user mappings.');
    }
  };

  const handleAutoMap = () => {
    const autoPairs: { jiraUserId: string; bugherdUserId: string }[] = [];

    jiraUsers.forEach(jiraUser => {
      const matchedBHUser = bugherdUsers.find(bhUser =>
        (jiraUser.email && bhUser.email?.toLowerCase() === jiraUser.email.toLowerCase()) ||
        bhUser.display_name?.toLowerCase() === (jiraUser.originalLabel || jiraUser.label).toLowerCase() ||
        bhUser.email?.toLowerCase() === (jiraUser.originalLabel || jiraUser.label).toLowerCase()
      );

      if (matchedBHUser) {
        autoPairs.push({
          jiraUserId: jiraUser.value,
          bugherdUserId: String(matchedBHUser.id)
        });
      }
    });

    if (autoPairs.length > 0) {
      setMappings(autoPairs);
      saveMappings(autoPairs);
      message.success(`Successfully mapped ${autoPairs.length} users.`);
    } else {
      message.info('No matching users found by name or email.');
    }
  };

  const bugherdUserOptions = bugherdUsers.map(user => ({
    label: user.display_name || user.email,
    value: String(user.id)
  }));

  const bugherdUserOptionsForMapping = (index: number) => {
    const selectedUserIds = mappings
      .filter((_mapping, mappingIndex) => mappingIndex !== index)
      .map(mapping => mapping.bugherdUserId)
      .filter(Boolean);

    return bugherdUserOptions.filter(
      option => !selectedUserIds.includes(option.value)
    );
  };

  const jiraUserOptionsForMapping = (index: number) => {
    const selectedUserIds = mappings
      .filter((_mapping, mappingIndex) => mappingIndex !== index)
      .map(mapping => mapping.jiraUserId)
      .filter(Boolean);

    return jiraUsers.filter(
      option => !selectedUserIds.includes(option.value)
    );
  };

  const renderMappingList = () => (
    <div className={styles.mappingList}>
      <div className={styles.mappingRowsContainer}>
        <Row gutter={16} className={styles.headerRow}>
          <Col span={10}><strong>BugHerd User</strong></Col>
          <Col span={2}></Col>
          <Col span={10}><strong>Jira User</strong></Col>
          <Col span={2}></Col>
        </Row>
        {mappings.map((mapping, index) => (
          <Row key={index} gutter={16} className={styles.mappingRow} align="middle">
            <Col span={10}>
              <Select
                showSearch
                style={{ width: '100%' }}
                placeholder="Search BugHerd user"
                optionFilterProp="label"
                value={mapping.bugherdUserId || undefined}
                onChange={value => handleMappingChange(index, 'bugherdUserId', value)}
                options={bugherdUserOptionsForMapping(index)}
              />
            </Col>
            <Col span={2} style={{ textAlign: 'center' }}>
              <Link size={16} className={styles.linkIcon} />
            </Col>
            <Col span={10}>
              <Select
                showSearch
                style={{ width: '100%' }}
                placeholder="Search Jira user"
                optionFilterProp="label"
                value={mapping.jiraUserId || undefined}
                onChange={value => handleMappingChange(index, 'jiraUserId', value)}
                options={jiraUserOptionsForMapping(index)}
              />
            </Col>
            <Col span={2}>
              <Button
                type="text"
                icon={<MinusCircleOutlined />}
                onClick={() => handleRemoveMapping(index)}
                danger
              />
            </Col>
          </Row>
        ))}
        <div className={styles.addMapping}>
          <Button type="link" onClick={handleAddMapping} icon={<PlusOutlined />}>
            Sync another user
          </Button>
        </div>
      </div>
      <div style={{ textAlign: 'center', marginTop: '20px' }}>
        <Divider plain>or</Divider>
        <Button
          type="primary"
          size="large"
          onClick={handleAutoMap}
          style={{ padding: '0 40px' }}
        >
          Automap BugHerd to Jira users
        </Button>
      </div>
    </div>
  );

  return (
    <Modal
      title="Manage users between BugHerd and Jira"
      open={open}
      onCancel={onClose}
      width={700}
      footer={null}
      getContainer={container}
    >
      <div className={styles.modalContent}>
        <p>This will allow you to match the users that are assigned and @mentioned on tasks across BugHerd and Jira.</p>

        {loading ? (
          <div className={styles.loader}>
            <Spin />
          </div>
        ) : renderMappingList()}
      </div>
    </Modal>
  );
};

export default JiraUserMappingModal;
